import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ROLE_LABELS, useAuth } from '../auth/AuthContext';
import ChangePasswordModal from '../auth/ChangePasswordModal';
import '../styles/Auth.css';

const LINKS = [
    { href: '/', label: 'Accueil', isActive: (path) => path === '/' },
    { href: '/clients', label: 'Clients', isActive: (path) => path.startsWith('/clients'), staffOnly: true },
    {
        href: '/outils/cbs', label: 'Outils', isActive: (path) => path.startsWith('/outils'), staffOnly: true,
        items: [
            { href: '/outils/cbs', label: "CBS d'un site" },
            { href: '/outils/origine-eau', label: "Origine de l'eau potable" },
        ],
    },
    { href: '/admin/usage-taxonomy', label: 'Admin', isActive: (path) => path.startsWith('/admin'), staffOnly: true },
];

const NavBar = () => {
    // Sur téléphone, les liens passent dans un menu déroulant ouvert par le bouton ☰.
    const [menuOpen, setMenuOpen] = useState(false);
    const navRef = useRef(null);
    const path = window.location.pathname;
    // Un compte client n'a ni la liste de tous les clients ni les réglages (l'API les lui refuse).
    const { user, isStaff } = useAuth();
    const links = LINKS.filter((link) => !link.staffOnly || !user || isStaff);

    useEffect(() => {
        if (!menuOpen) return;
        const handleClickOutside = (e) => {
            if (navRef.current && !navRef.current.contains(e.target)) setMenuOpen(false);
        };
        const handleEscape = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [menuOpen]);

    return (
        <nav className="nav-bar" ref={navRef}>
            <a href="/" className="nav-bar-brand">
                <img src="/brand/walter-logo.svg" alt="Walter by Hydros" className="nav-bar-logo-img" />
            </a>
            <button
                type="button"
                className={`nav-bar-burger${menuOpen ? ' nav-bar-burger--open' : ''}`}
                onClick={() => setMenuOpen(open => !open)}
                aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                aria-expanded={menuOpen}
                aria-controls="nav-bar-links"
            >
                <span />
                <span />
                <span />
            </button>
            <div id="nav-bar-links" className={`nav-bar-links${menuOpen ? ' nav-bar-links--open' : ''}`}>
                {links.map(link => link.items ? (
                    <NavDropdown key={link.href} link={link} active={link.isActive(path)} onNavigate={() => setMenuOpen(false)} />
                ) : (
                    <a
                        key={link.href}
                        href={link.href}
                        className={`nav-bar-link${link.isActive(path) ? ' active' : ''}`}
                        onClick={() => setMenuOpen(false)}
                    >
                        {link.label}
                    </a>
                ))}
                <NavUserMenu onNavigate={() => setMenuOpen(false)} />
            </div>
        </nav>
    );
};

// Entrée du bandeau qui ouvre une liste de pages (Outils).
const NavDropdown = ({ link, active, onNavigate }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    return (
        <div className="nav-user nav-dropdown" ref={ref}>
            <button
                type="button" className={`nav-bar-link${active ? ' active' : ''}`}
                onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-haspopup="menu"
            >
                {link.label} ▾
            </button>
            {open && (
                <div className="nav-user-menu nav-dropdown-menu" role="menu">
                    {link.items.map((item) => (
                        <a key={item.href} href={item.href} className="nav-user-item" role="menuitem"
                            onClick={() => { setOpen(false); onNavigate(); }}>
                            {item.label}
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
};

function initials(name) {
    return (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join('');
}

// Compte connecté : nom, rôle, changement de mot de passe, page Comptes (admin), déconnexion.
// Sans compte : lien « Se connecter » (tant que l'API n'impose pas la connexion, l'app reste utilisable sans).
const NavUserMenu = ({ onNavigate }) => {
    const { user, isAdmin, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    if (!user) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        return <a href={`/login?next=${next}`} className="nav-bar-link" onClick={onNavigate}>Se connecter</a>;
    }

    const handleLogout = async () => {
        setOpen(false);
        try {
            const result = await logout();
            if (result.blocked) {
                window.alert(`${result.blocked} modification(s) faite(s) hors-ligne ne sont pas encore synchronisées. `
                    + 'Reconnectez-vous au réseau et attendez la fin de la synchronisation avant de vous déconnecter.');
                return;
            }
            window.location.assign('/');
        } catch (error) {
            window.alert(`Déconnexion impossible : ${error.message}`);
        }
    };

    return (
        <div className="nav-user" ref={menuRef}>
            <button
                type="button" className="nav-user-button" onClick={() => setOpen((value) => !value)}
                aria-expanded={open} aria-haspopup="menu"
            >
                <span className="nav-user-avatar">{initials(user.name)}</span>
                <span className="nav-user-name">{user.name}</span>
            </button>
            {open && (
                <div className="nav-user-menu" role="menu">
                    <div className="nav-user-identity">
                        <strong>{user.name}</strong>
                        <span>{user.email}</span>
                        <span className="nav-user-role">{ROLE_LABELS[user.role] || user.role}</span>
                    </div>
                    <a href="/compte" className="nav-user-item" role="menuitem" onClick={onNavigate}>Mon compte et assistant Claude</a>
                    {isAdmin && (
                        <a href="/admin/users" className="nav-user-item" role="menuitem" onClick={onNavigate}>Comptes de l'équipe</a>
                    )}
                    <button type="button" className="nav-user-item" role="menuitem"
                        onClick={() => { setOpen(false); setChangingPassword(true); }}>
                        Changer de mot de passe
                    </button>
                    <button type="button" className="nav-user-item nav-user-item--danger" role="menuitem" onClick={handleLogout}>
                        Se déconnecter
                    </button>
                </div>
            )}
            {/* Portail : la modale ne doit pas dépendre du menu ☰ (masqué une fois refermé sur téléphone). */}
            {changingPassword && createPortal(<ChangePasswordModal onClose={() => setChangingPassword(false)} />, document.body)}
        </div>
    );
};

export default NavBar;
